// src/pages/players/PlayersPage.tsx — "Record Book" player register + scouting card
import React, { useState, useMemo } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { useApi } from '../../hooks/useApi';
import apiService from '../../services/api';
import { Player, PlayerFilters } from '../../types';
import PlayerScoutingCard, { Headshot, positionChip } from '../../components/players/PlayerScoutingCard';
import { bySeasonAverageDesc, isElite, seasonAverage } from '../../utils/playerSeason';

type SortOption = 'fantasy_points_avg' | 'name' | 'team' | 'experience';
type PositionFilter = 'all' | 'K' | 'P';


export const PlayersPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState<PlayerFilters>({ position: 'all', search: '' });
  const [sortBy, setSortBy] = useState<SortOption>('fantasy_points_avg');
  const [showFilters, setShowFilters] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);

  const apiFilters: PlayerFilters = useMemo(
    () => ({
      position: filters.position === 'all' ? undefined : filters.position,
      search: searchTerm || undefined,
      limit: 200,
    }),
    [filters.position, searchTerm]
  );

  const { data: players, loading, error, refetch } = useApi(
    () => apiService.getPlayers(apiFilters),
    [apiFilters.position, apiFilters.search]
  );

  const sortedPlayers = useMemo(() => {
    if (!players) return [];
    return [...players].sort((a, b) => {
      switch (sortBy) {
        case 'fantasy_points_avg':
          // Ranks on this season only. Players who haven't taken the field sink
          // to the bottom instead of floating up on last season's average.
          return bySeasonAverageDesc(a, b);
        case 'name':
          return a.name.localeCompare(b.name);
        case 'team':
          return (a.nfl_team?.name || a.nflTeam?.name || '').localeCompare(b.nfl_team?.name || b.nflTeam?.name || '');
        case 'experience':
          return (b.experience || b.experienceYears || 0) - (a.experience || a.experienceYears || 0);
        default:
          return 0;
      }
    });
  }, [players, sortBy]);

  const setPosition = (position: PositionFilter) => setFilters(prev => ({ ...prev, position }));
  const clearFilters = () => {
    setSearchTerm('');
    setFilters({ position: 'all' });
  };

  const kCount = sortedPlayers.filter(p => p.position === 'K').length;
  const pCount = sortedPlayers.filter(p => p.position === 'P').length;

  const Masthead = (
    <div className="border-b-2 border-ink-900 pb-3">
      <h1 className="font-display uppercase text-ink-900 text-4xl leading-none">Player Research</h1>
      <p className="text-secondary-600 mt-1">
        Explore real NFL kickers and punters with live stats and team information.
      </p>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-8">
        {Masthead}
        <ul className="divide-y divide-secondary-200 border-y border-secondary-200">
          {Array.from({ length: 8 }).map((_, i) => (
            <li key={i} className="flex items-center gap-4 py-3 px-2 animate-pulse">
              <div className="w-12 h-12 bg-secondary-200 shrink-0" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-40 bg-secondary-200" />
                <div className="h-3 w-28 bg-secondary-100" />
              </div>
              <div className="h-6 w-10 bg-secondary-200" />
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-8">
        {Masthead}
        <div className="py-12 text-center">
          <p className="font-display uppercase text-lg text-ink-900">Couldn’t load players</p>
          <p className="mt-1 text-sm text-secondary-600">Something interrupted the connection. Give it another try.</p>
          <button
            onClick={() => refetch().catch(() => {})}
            className="mt-4 inline-flex items-center gap-1.5 border-2 border-ink-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-ink-900 hover:text-paper transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const positionTabs: { key: PositionFilter; label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'K', label: 'Kickers' },
    { key: 'P', label: 'Punters' },
  ];

  return (
    <div className="space-y-8">
      {Masthead}

      {/* ── Toolbar ── */}
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Search players, teams, or colleges…"
              className="form-input pl-10"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="form-select sm:w-52"
            value={sortBy}
            onChange={e => setSortBy(e.target.value as SortOption)}
          >
            <option value="fantasy_points_avg">Sort by Fantasy Avg</option>
            <option value="name">Sort by Name</option>
            <option value="team">Sort by Team</option>
            <option value="experience">Sort by Experience</option>
          </select>
          <button onClick={() => setShowFilters(v => !v)} className="btn-outline-secondary flex items-center">
            <FunnelIcon className="w-4 h-4 mr-2" />
            Filters
            {filters.stadium_type && filters.stadium_type !== 'all' && (
              <span className="ml-2 w-2 h-2 bg-gold rounded-none" />
            )}
          </button>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          {/* Position segmented control */}
          <div className="inline-flex border border-secondary-300">
            {positionTabs.map(t => {
              const active = (filters.position || 'all') === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setPosition(t.key)}
                  className={`px-3.5 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                    active ? 'bg-ink-900 text-paper' : 'text-secondary-600 hover:text-ink-900'
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-3 text-sm text-secondary-500">
            <span>
              <span className="font-display text-base text-ink-900">{sortedPlayers.length}</span> players · {kCount} K · {pCount} P
            </span>
            <button
              onClick={() => refetch()}
              className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-secondary-600 hover:text-ink-900 transition-colors"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {showFilters && (
          <div className="flex flex-wrap items-end gap-4 border-t border-secondary-200 pt-4">
            <div>
              <label className="form-label">Stadium Type</label>
              <select
                className="form-select"
                value={filters.stadium_type || 'all'}
                onChange={e => setFilters(prev => ({ ...prev, stadium_type: e.target.value as PlayerFilters['stadium_type'] }))}
              >
                <option value="all">All Stadiums</option>
                <option value="dome">Dome Only</option>
                <option value="outdoor">Outdoor Only</option>
                <option value="retractable">Retractable Roof</option>
              </select>
            </div>
            <button onClick={clearFilters} className="btn-outline-secondary btn-sm">
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* ── Register ── */}
      {sortedPlayers.length === 0 ? (
        <div className="py-16 text-center">
          <MagnifyingGlassIcon className="w-12 h-12 text-secondary-300 mx-auto mb-3" />
          <p className="font-display uppercase text-lg text-ink-900">No players found</p>
          <p className="mt-1 mb-5 text-sm text-secondary-600">
            {searchTerm ? `No players match “${searchTerm}”.` : 'Try clearing your filters.'}
          </p>
          <button
            onClick={clearFilters}
            className="inline-flex items-center gap-1.5 border-2 border-ink-900 px-4 py-2 text-xs font-bold uppercase tracking-wider text-ink-900 hover:bg-ink-900 hover:text-paper transition-colors"
          >
            Clear Search & Filters
          </button>
        </div>
      ) : (
        <ul className="divide-y divide-secondary-200 border-y border-secondary-200">
          {sortedPlayers.map(player => {
            const team = player.nfl_team || player.nflTeam;
            const jersey = player.jersey_number || player.jerseyNumber;
            const experience = player.experience || player.experienceYears || 0;
            const season = seasonAverage(player);
            return (
              <li key={player.id}>
                <button
                  onClick={() => setSelectedPlayer(player)}
                  className="group w-full flex items-center gap-4 py-3 px-2 -mx-2 text-left hover:bg-secondary-50 transition-colors"
                >
                  <Headshot player={player} team={team} jersey={jersey} size="sm" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                      <span className="font-semibold text-ink-900 truncate">{player.name}</span>
                      <span className={`px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase ${positionChip(player.position)}`}>
                        {player.position}
                      </span>
                      <span className="text-xs text-secondary-500">#{jersey || '?'}</span>
                      {isElite(season) && <StarIconSolid className="w-3.5 h-3.5 text-gold-600" title="Top performer" />}
                    </div>
                    <div className="mt-0.5 text-sm text-secondary-500">
                      {team?.abbreviation || team?.city || '—'} · {experience} yr{experience === 1 ? '' : 's'} exp
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-2xl leading-none text-ink-900 tabular-nums">{season.display}</div>
                    <div className="text-[0.6rem] font-semibold uppercase tracking-wider text-secondary-500">
                      {season.label}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* ── From the Press Box ── */}
      <section className="bg-ink-900 text-paper on-ink p-7 sm:p-9">
        <div className="flex flex-wrap items-baseline gap-x-4 mb-5">
          <h2 className="font-display uppercase text-paper text-2xl leading-none">From the Press Box</h2>
          <span className="text-xs font-semibold uppercase tracking-[0.18em] text-gold">Scouting</span>
        </div>
        <ul className="grid sm:grid-cols-2 gap-x-10 gap-y-3 text-paper-300 leading-relaxed">
          <li><span className="text-paper font-semibold">Dome kickers</span> stay consistent when the weather turns ugly.</li>
          <li><span className="text-paper font-semibold">High-scoring offenses</span> manufacture more field-goal chances.</li>
          <li><span className="text-paper font-semibold">Strong legs</span> cash in on 50+ yard attempts and distance bonuses.</li>
          <li><span className="text-paper font-semibold">Punters on bad teams</span> simply get more chances to punt.</li>
          <li><span className="text-paper font-semibold">Inside-the-20 placement</span> is where punters earn their keep.</li>
          <li><span className="text-paper font-semibold">Wind and rain</span> swing outdoor leg performance more than anything.</li>
        </ul>
      </section>

      {selectedPlayer && (
        <PlayerScoutingCard player={selectedPlayer} onClose={() => setSelectedPlayer(null)} />
      )}
    </div>
  );
};

export default PlayersPage;
